import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../organisation/useMe';
import { caseInternalApi } from '../apis';
import {
  InternalApiAttachToCasesRequest,
  InternalApiAttributeType,
  InternalApiCaseAttachmentType,
  InternalApiLogicalOperator,
  InternalApiSearchFilter,
  InternalApiSearchOperator,
  InternalApiSortDirection,
} from '../api-internal';
import { CASE_ALERTS_QUERY_KEY } from './attachments/alerts/caseAlertQueryActions';
import { CASE_PERSONS_QUERY_KEY } from './attachments/persons/casePersonQueryActions';
import { CASE_TRANSACTIONS_QUERY_KEY } from './attachments/transactions/caseTransactionQueryActions';

export const CASES_QUERY_KEY = 'cases';

const PAGE_SIZE = 20;

export interface CaseSearchQuery {
  filters: InternalApiSearchFilter[];
  logicalOperator: InternalApiLogicalOperator;
  page: number;
  orderBy?: string;
  direction?: InternalApiSortDirection;
}

export const useQueryCasesPage = ({
  filters = [],
  logicalOperator = InternalApiLogicalOperator.AND,
  page,
  orderBy,
  direction,
}: CaseSearchQuery) => {
  const { currentOrganisation } = useMe();
  const searchQuery = { filters, logicalOperator, page, orderBy, direction };

  return useQuery({
    queryKey: [CASES_QUERY_KEY, 'page', JSON.stringify(searchQuery), currentOrganisation?.id],
    queryFn: async () =>
      await caseInternalApi.findCasesPage({
        internalApiFindCasesPageRequest: {
          page,
          size: PAGE_SIZE,
          orderBy,
          direction,
          filters,
          logicalOperator,
        },
      }),
    placeholderData: keepPreviousData,
    enabled: !!currentOrganisation?.id,
  });
};

const ATTACHMENT_TYPE_ATTRIBUTE_IDENTIFIER: Record<InternalApiCaseAttachmentType, string> = {
  [InternalApiCaseAttachmentType.PERSON]: 'personExternalId',
  [InternalApiCaseAttachmentType.TRANSACTION]: 'transactionExternalId',
  [InternalApiCaseAttachmentType.SCREENING_ALERT]: 'screeningAlertExternalId',
  [InternalApiCaseAttachmentType.MONITORING_ALERT]: 'monitoringAlertExternalId',
  [InternalApiCaseAttachmentType.BRIDGE_ALERT]: 'bridgeAlertExternalId',
  [InternalApiCaseAttachmentType.MANUAL_ALERT]: 'manualAlertExternalId',
};

interface AttachmentCasesParams {
  attachmentType: InternalApiCaseAttachmentType;
  externalId: string;
  page: number;
  size: number;
  orderBy?: string;
  direction?: InternalApiSortDirection;
}

export const useQueryCasesWithAttachment = ({
  attachmentType,
  externalId,
  page,
  size,
  orderBy,
  direction,
}: AttachmentCasesParams) => {
  const { currentOrganisation } = useMe();
  const attributeIdentifier = ATTACHMENT_TYPE_ATTRIBUTE_IDENTIFIER[attachmentType];

  return useQuery({
    queryKey: [CASES_QUERY_KEY, attachmentType, externalId, page, size, orderBy, direction, currentOrganisation?.id],
    queryFn: async () =>
      await caseInternalApi.findCasesPage({
        internalApiFindCasesPageRequest: {
          page,
          size,
          orderBy,
          direction,
          filters: [
            {
              attributeIdentifier,
              attributeType: InternalApiAttributeType.GENERAL,
              operator: InternalApiSearchOperator.IS_EXACTLY,
              not: false,
              query: externalId,
            },
          ],
          logicalOperator: InternalApiLogicalOperator.AND,
        },
      }),
    placeholderData: keepPreviousData,
    enabled: !!currentOrganisation?.id,
  });
};

export const useQueryCaseHandles = (attachmentType: InternalApiCaseAttachmentType, externalId: string) => {
  const { currentOrganisation } = useMe();
  const attributeIdentifier = ATTACHMENT_TYPE_ATTRIBUTE_IDENTIFIER[attachmentType];

  return useQuery({
    queryKey: [CASES_QUERY_KEY, 'handles', attachmentType, externalId, currentOrganisation?.id],
    queryFn: async () =>
      await caseInternalApi.findCaseHandles({
        internalApiFindCaseHandlesRequest: {
          filters: [
            {
              attributeIdentifier,
              attributeType: InternalApiAttributeType.GENERAL,
              operator: InternalApiSearchOperator.IS_EXACTLY,
              not: false,
              query: externalId,
            },
          ],
          logicalOperator: InternalApiLogicalOperator.AND,
        },
      }),
    enabled: !!currentOrganisation?.id,
  });
};

export const useAttachToCases = (type: InternalApiCaseAttachmentType) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Pick<InternalApiAttachToCasesRequest, 'ids' | 'caseHandles'>) => {
      return caseInternalApi.attachToCases({ internalApiAttachToCasesRequest: { ...request, type } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CASES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CASE_PERSONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CASE_TRANSACTIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CASE_ALERTS_QUERY_KEY] });
    },
  });
};
